import { useCallback, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useUploadMutation } from "@/hooks/uploads/useUploadMutation";
import {
  validateDocumentFile,
  validateUploadFileSize,
  getErrorMessage,
  getTooLargeMessage,
  formatFileSize,
} from "@/hooks/uploads/uploadValidation";
import { uploadFileToStorage } from "@/lib/uploadFileToStorage";
import AgentSelection from "../AgentSelection";
import { UploadComponentProps } from "./types";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface UploadDocumentProps extends UploadComponentProps {
  documentFile: File | null;
  onDocumentFileChange: (file: File | null) => void;
}

const UploadDocument = ({
  availableAgents,
  selectedAgentId,
  onAgentSelect,
  documentFile,
  onDocumentFileChange,
  onUploadSuccess,
  onDeferredActionAdd,
}: UploadDocumentProps) => {
  const documentExtractMutation = useUploadMutation("document", selectedAgentId, onUploadSuccess);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);

  const uploadDocumentFile = useCallback(
    async (file: File) => {
      try {
        setIsUploadingDocument(true);
        const uploadToast = toast.loading(`Uploading "${file.name}"...`);

        const { fileUrl } = await uploadFileToStorage(file, "temp-uploads");

        toast.dismiss(uploadToast);

        onDeferredActionAdd({
          type: "document",
          data: {
            fileUrl,
            fileName: file.name,
          },
        });
        toast.success(`"${file.name}" uploaded and queued for extraction`);
        onDocumentFileChange(null);
      } catch (error) {
        toast.error(`Failed to upload "${file.name}"`);
        console.error("Upload error:", error);
      } finally {
        setIsUploadingDocument(false);
      }
    },
    [onDeferredActionAdd, onDocumentFileChange],
  );

  const handleDocumentFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!validateDocumentFile(file)) {
        toast.error(getErrorMessage("document"));
        return;
      }

      if (!validateUploadFileSize(file)) {
        toast.error(getTooLargeMessage(file));
        return;
      }

      if (onDeferredActionAdd) {
        await uploadDocumentFile(file);
        e.target.value = "";
      } else {
        onDocumentFileChange(file);
      }
    },
    [onDocumentFileChange, onDeferredActionAdd, uploadDocumentFile],
  );

  const handleDocumentDrop = useCallback(
    async (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      if (!file) return;

      if (!validateDocumentFile(file)) {
        toast.error(getErrorMessage("document"));
        return;
      }

      if (!validateUploadFileSize(file)) {
        toast.error(getTooLargeMessage(file));
        return;
      }

      if (onDeferredActionAdd) {
        await uploadDocumentFile(file);
      } else {
        onDocumentFileChange(file);
      }
    },
    [onDocumentFileChange, onDeferredActionAdd, uploadDocumentFile],
  );

  const handleDocumentDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
  };

  return (
    <>
      <AgentSelection
        agents={availableAgents}
        selectedAgentId={selectedAgentId}
        onAgentSelect={onAgentSelect}
      />
      <div className="flex flex-col items-center justify-center h-full max-w-2xl mx-auto">
        <GenieIcon name="document" size={64} color="#4E9CFF" gradientEnabled className="mb-4" />
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Extract Document Text</h3>
        <p className="text-sm text-gray-600 mb-6 text-center">Upload TXT, CSV, DOC, or DOCX files</p>

        <input
          type="file"
          accept=".txt,.csv,.doc,.docx"
          onChange={handleDocumentFileChange}
          className="hidden"
          id="document-file-input"
        />
        <label
          htmlFor="document-file-input"
          onDrop={handleDocumentDrop}
          onDragOver={handleDocumentDragOver}
          className="flex items-center justify-center w-full max-w-md h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-genie-primary transition-colors bg-gray-50"
        >
          {documentFile ? (
            <div className="text-center">
              <p className="text-sm font-medium text-gray-700">{documentFile.name}</p>
              <p className="text-xs text-gray-500 mt-1">{formatFileSize(documentFile.size)}</p>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-sm text-gray-600">Click or drag to upload document</p>
              <p className="text-xs text-gray-500 mt-1">TXT, CSV, DOC, or DOCX</p>
            </div>
          )}
        </label>
        {documentFile && (
          <Button
            onClick={() => {
              if (onDeferredActionAdd) {
                uploadDocumentFile(documentFile);
              } else {
                documentExtractMutation.mutate({ file: documentFile });
              }
            }}
            disabled={!documentFile || documentExtractMutation.isPending || isUploadingDocument}
            className="mt-4 bg-gradient-to-r from-genie-primary to-genie-secondary text-white font-semibold px-8 h-12 shadow-lg hover:shadow-xl transition-all hover:scale-105 rounded-xl"
          >
            {documentExtractMutation.isPending || isUploadingDocument ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                {isUploadingDocument ? "Uploading..." : "Extracting..."}
              </>
            ) : (
              onDeferredActionAdd ? "Upload Document" : "Extract Text"
            )}
          </Button>
        )}
      </div>
    </>
  );
};

export default UploadDocument;
